import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertTriangle, CheckCircle, RefreshCw, ExternalLink } from 'lucide-react';
import { walletAPI } from '../services/api';

function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get('payment_id');
  const provider = searchParams.get('provider') || 'payfast';
  const isNativeApp = Capacitor.isNativePlatform();
  const appReturn = searchParams.get('app_return') === '1' && !isNativeApp;
  const [status, setStatus] = useState('verifying');
  const [amount, setAmount] = useState(null);
  const [retry, setRetry] = useState(0);

  const goToDashboard = useCallback(() => {
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  const openCaddiePayApp = () => {
    const query = searchParams.toString();
    const targetPath = `payment/success${query ? `?${query}` : ''}`;
    const targetUrl = /android/i.test(window.navigator.userAgent)
      ? `intent://${targetPath}#Intent;scheme=app.caddiepay.mobile;package=app.caddiepay.mobile;S.browser_fallback_url=${encodeURIComponent(window.location.href)};end`
      : `app.caddiepay.mobile://${targetPath}`;

    window.location.href = targetUrl;
  };

  useEffect(() => {
    let attempts = 0;
    let cancelled = false;
    let retryTimer;
    const maxAttempts = 3;
    setStatus('verifying');
    setAmount(null);

    const verifyPayment = async () => {
      if (!paymentId) {
        setStatus('missing_reference');
        return;
      }
      try {
        const resp = provider === 'payfast'
          ? await walletAPI.verifyPayfastPayment(paymentId)
          : await walletAPI.verifyGatewayTopup(provider, paymentId);
        if (cancelled) return;
        if (resp.data.status === 'credited' || resp.data.status === 'already_credited') {
          setStatus('credited');
          setAmount(resp.data.amount);
        } else if (resp.data.status === 'credit_reconciliation_required') {
          setStatus('needs_support');
          setAmount(resp.data.amount);
        } else if (resp.data.status === 'pending_provider_confirmation' || resp.data.status === 'credit_in_progress') {
          attempts++;
          if (attempts < maxAttempts) {
            retryTimer = setTimeout(verifyPayment, 3000);
          } else {
            setStatus('pending_confirmation');
            setAmount(resp.data.amount);
          }
        } else {
          attempts++;
          if (attempts < maxAttempts) {
            retryTimer = setTimeout(verifyPayment, 3000);
          } else {
            setStatus('pending_confirmation');
          }
        }
      } catch {
        if (!cancelled) setStatus('pending_confirmation');
      }
    };

    verifyPayment();
    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [paymentId, provider, retry]);

  useEffect(() => {
    if (status !== 'credited' || appReturn) {
      return undefined;
    }

    const redirectDelayMs = isNativeApp ? 1500 : 4000;
    const redirectTimer = window.setTimeout(goToDashboard, redirectDelayMs);

    return () => {
      window.clearTimeout(redirectTimer);
    };
  }, [appReturn, goToDashboard, isNativeApp, status]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md text-center border-0 shadow-lg" data-testid="payment-success-card">
        <CardHeader className="pb-2">
          {status === 'verifying' ? (
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RefreshCw className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
          ) : status !== 'credited' ? (
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-10 h-10 text-amber-700" />
            </div>
          ) : (
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
          )}
          <CardTitle className={`text-2xl ${status === 'credited' ? 'text-emerald-900' : 'text-amber-900'}`}>
            {status === 'verifying'
              ? 'Verifying Payment...'
              : status === 'needs_support'
                ? 'Payment Needs Review'
              : status === 'pending_confirmation'
                ? 'Payment Confirmation Pending'
              : status === 'missing_reference'
                ? 'Payment Reference Missing'
                : 'Payment Successful'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'credited' && amount && (
            <p className="text-lg font-semibold text-emerald-700">R{amount.toFixed(2)} added to your wallet</p>
          )}
          <p className="text-slate-600">
            {status === 'verifying'
              ? 'Confirming your payment with the provider...'
              : status === 'needs_support'
                ? 'We could not safely confirm the wallet credit. Please contact support and quote your payment reference. Do not make another payment.'
              : status === 'pending_confirmation'
                ? 'We are waiting for confirmed settlement from the payment provider. Your wallet will update automatically once confirmation is received.'
              : status === 'missing_reference'
                ? 'We cannot check this payment without its reference. Check your wallet or contact support before making another payment.'
              : appReturn
                ? 'Your wallet has been updated. Return to the app to continue.'
                : 'Your wallet has been updated. Redirecting to dashboard...'}
          </p>
          {paymentId && (status === 'pending_confirmation' || status === 'needs_support') && (
            <>
              <p className="text-sm text-slate-500 break-all">Payment reference: {paymentId}</p>
              <Button onClick={() => setRetry(value => value + 1)} className="w-full" variant="outline">
                Check Again
              </Button>
            </>
          )}
          {appReturn ? (
            <Button onClick={openCaddiePayApp} className="w-full btn-primary" data-testid="open-app-btn">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open CaddiePay
            </Button>
          ) : (
            <Button onClick={goToDashboard} className="w-full btn-primary" data-testid="back-to-dashboard-btn">
              Go to Dashboard
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default PaymentSuccess;
